import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { SearchableMultiSelect } from "@/components/ui/searchable-multi-select";
import { toast } from "sonner";
import {
  Flag, AlertTriangle, CheckCircle2, XCircle, Clock,
  ExternalLink, Loader2, Settings, Mail, Plus, Trash2,
  RefreshCw, ChevronLeft, ChevronRight, Eye, Search, Filter, Eraser, Combine
} from "lucide-react";
import { formatDateIST } from "@/utils/dateUtils";
import { can } from "@/lib/permissions";

const PRIORITY_COLORS = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-yellow-100 text-yellow-700",
  high: "bg-orange-100 text-orange-700",
  critical: "bg-red-100 text-red-700"
};

const STATUS_COLORS = {
  active: "bg-red-100 text-red-700",
  resolved: "bg-green-100 text-green-700",
  ignored: "bg-slate-100 text-slate-700"
};

const PRIORITY_LABELS = { critical: "Critical", high: "High", medium: "Medium", low: "Low" };

// Order-detail deep link (opens in a NEW browser tab from cards/modal).
const orderDetailUrl = (flag) => `/admin/orders/${flag?.enrichment?.order_id || flag?.entity_id || ""}`;

// Human appointment string from the enriched order fields.
const apptDisplay = (en) => {
  if (!en) return "";
  if (en.appointment_date) return `${en.appointment_date}${en.appointment_time_range ? ` · ${en.appointment_time_range}` : ""}`;
  if (en.appointment_datetime) return formatDateIST(en.appointment_datetime, "full");
  return "";
};

const stop = (e) => e.stopPropagation();

// Persist the page's view state (view mode + active tab + filters + search + page) to
// localStorage so it survives navigating away / refresh.
const LS_KEY = "opsRedFlags.state.v1";
const loadPersisted = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}") || {}; }
  catch { return {}; }
};

// The chronologically latest action log. A merged flag's action_logs are appended keeper-first
// then per-loser, so the last array element isn't necessarily the newest — pick by action_at.
const latestActionLog = (logs) => {
  if (!logs || !logs.length) return null;
  return logs.reduce((a, b) =>
    (new Date(b?.action_at || 0).getTime() >= new Date(a?.action_at || 0).getTime() ? b : a));
};

const RedFlags = ({ user, onLogout }) => {
  const [persisted] = useState(loadPersisted);   // restore once on mount
  const [loading, setLoading] = useState(true);
  const [flags, setFlags] = useState([]);
  const [pagination, setPagination] = useState({ page: persisted.page || 1, limit: 20, total: 0, pages: 0 });
  const [statusFilter, setStatusFilter] = useState(persisted.statusFilter || "active");
  // Flags view: flat list, or grouped by LRN / by Client.
  const [viewMode, setViewMode] = useState(persisted.viewMode || "list"); // list | lrn | client
  const [allFlags, setAllFlags] = useState([]);        // full matching set for grouped views
  const [allFlagsLoading, setAllFlagsLoading] = useState(false);
  // Multi-select filters (arrays). Action Status stays single (mutually-exclusive tri-state).
  const [priorityFilters, setPriorityFilters] = useState(persisted.priorityFilters || []);
  const [flagTypeFilters, setFlagTypeFilters] = useState(persisted.flagTypeFilters || []);
  const [actionFilter, setActionFilter] = useState(persisted.actionFilter || "all"); // "all" | "needs_attention" | "action_taken"
  // Resolution source — only relevant on resolved/ignored tabs. "auto" = system cron auto-resolved.
  const [resolutionFilter, setResolutionFilter] = useState(persisted.resolutionFilter || "all"); // "all" | "auto" | "manual"

  // Search + entity filters
  const [searchQuery, setSearchQuery] = useState(persisted.searchQuery || "");
  const [debouncedSearch, setDebouncedSearch] = useState(persisted.searchQuery || "");
  const [sellerFilters, setSellerFilters] = useState(persisted.sellerFilters || []);
  const [shippingPartnerFilters, setShippingPartnerFilters] = useState(persisted.shippingPartnerFilters || []);
  // Dropdown options come from the values actually present in the flags (this status), not
  // the full master lists — fetched from /red-flags/filter-options.
  const [filterOptions, setFilterOptions] = useState({ sellers: [], shipping_partners: [], flag_types: [], priorities: [], summary: { total: 0, by_flag_type: [], by_seller: [] } });
  // Collapsible filter panel — toggled by the filter icon; the search bar stays visible.
  const [filtersExpanded, setFiltersExpanded] = useState(persisted.filtersExpanded || false);

  // Status counts for tabs
  const [statusCounts, setStatusCounts] = useState({ active: 0, resolved: 0, ignored: 0 });
  
  // Config states
  const [flagTypes, setFlagTypes] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [availableActions, setAvailableActions] = useState([]);
  
  // Settings states
  const [settings, setSettings] = useState(null);
  const [checkInterval, setCheckInterval] = useState(15);
  const [savingSettings, setSavingSettings] = useState(false);
  const [triggeringCheck, setTriggeringCheck] = useState(false);
  
  // Dialog states
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [actionType, setActionType] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  const [submittingAction, setSubmittingAction] = useState(false);
  
  // Config dialog states
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [editingFlagType, setEditingFlagType] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Recipient dialog states
  const [recipientDialogOpen, setRecipientDialogOpen] = useState(false);
  const [newRecipientEmail, setNewRecipientEmail] = useState("");
  const [newRecipientName, setNewRecipientName] = useState("");
  const [savingRecipient, setSavingRecipient] = useState(false);
  
  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [flagDetail, setFlagDetail] = useState(null);
  
  const isAdmin = user?.role === "admin";
  // Bulk-resolve delivered is permission-gated (red_flags:act); admins always allowed.
  const canBulkResolve = isAdmin || can(user, "red_flags:act");
  const [bulkResolving, setBulkResolving] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [cleaningStuck, setCleaningStuck] = useState(false);
  const [merging, setMerging] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkPreview, setBulkPreview] = useState({ count: 0, flags: [] });
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);

  // Open the modal and load the preview of what would be resolved (LRN, delivery info, etc.).
  const openBulkResolve = async () => {
    setBulkModalOpen(true);
    setBulkPreviewLoading(true);
    setBulkPreview({ count: 0, flags: [] });
    try {
      const res = await axios.get(`${API}/red-flags/bulk-resolve-delivered/preview`);
      setBulkPreview(res.data || { count: 0, flags: [] });
    } catch (error) {
      toast.error("Failed to load preview");
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const confirmBulkResolve = async () => {
    setBulkResolving(true);
    try {
      const res = await axios.post(`${API}/red-flags/bulk-resolve-delivered`);
      toast.success(res.data.message || `Resolved ${res.data.resolved} flag(s)`);
      setBulkModalOpen(false);
      fetchFlags();
      fetchStatusCounts();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to bulk-resolve");
    } finally {
      setBulkResolving(false);
    }
  };

  // Collapse duplicate active flags (keep one per order + flag type) and add the guard index.
  const handleDedupe = async () => {
    if (!window.confirm("Remove duplicate active flags? For each order + flag type, one flag is kept and the rest are resolved.")) return;
    setDeduping(true);
    try {
      const res = await axios.post(`${API}/red-flags/dedupe`);
      toast.success(res.data.message || `Removed ${res.data.removed} duplicate(s)`);
      fetchFlags();
      if (viewMode !== "list") fetchAllFlags();
      fetchStatusCounts();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove duplicates");
    } finally {
      setDeduping(false);
    }
  };

  // Merge every LRN that has multiple active flags into one consolidated flag (all reasons +
  // all comments combined). Folded flags stay active-but-hidden so the checker won't recreate them.
  const handleMergeFlags = async () => {
    if (!window.confirm("Merge flags per LRN? Every order with more than one active flag becomes a single flag showing all its reasons and comments. This can be re-run as new flags appear.")) return;
    setMerging(true);
    try {
      const res = await axios.post(`${API}/red-flags/merge`);
      toast.success(res.data.message || `Merged ${res.data.folded} flag(s)`);
      fetchFlags();
      if (viewMode !== "list") fetchAllFlags();
      fetchStatusCounts();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to merge flags");
    } finally {
      setMerging(false);
    }
  };

  // Clean the redundant 'Order Stuck in Same Status' flags for REACH_DESTINATION — that case is
  // already covered by the dedicated 'Stuck at Last Mile Center' flag, so the generic one is noise.
  const handleCleanupReachStuck = async () => {
    if (!window.confirm("Clean redundant 'Order Stuck in Same Status' flags for REACH_DESTINATION? These are already covered by the 'Stuck at Last Mile Center' flag. No-action flags are removed; actioned ones are resolved.")) return;
    setCleaningStuck(true);
    try {
      const res = await axios.post(`${API}/red-flags/cleanup-reach-destination-stuck`);
      toast.success(res.data.message || `Cleaned ${res.data.removed} redundant flag(s)`);
      fetchFlags();
      if (viewMode !== "list") fetchAllFlags();
      fetchStatusCounts();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clean redundant flags");
    } finally {
      setCleaningStuck(false);
    }
  };

  // Reset to page 1 on a filter change. Called from the filter setters so it batches with
  // the setter in one render — the fetch effect then runs once, not once on the old page
  // and again after the reset.
  const resetPage = () => setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));

  // Active filter count (shown as a badge on the collapsed filter icon). Search is separate
  // (always visible), so it isn't counted here.
  const activeFilterCount =
    priorityFilters.length + flagTypeFilters.length + sellerFilters.length +
    shippingPartnerFilters.length + (actionFilter && actionFilter !== "all" ? 1 : 0) +
    (statusFilter !== "active" && resolutionFilter !== "all" ? 1 : 0);

  // Stable primitive keys so the array filters can drive the effect dep list.
  const priKey = priorityFilters.join(",");
  const ftKey = flagTypeFilters.join(",");
  const sellerKey = sellerFilters.join(",");
  const spKey = shippingPartnerFilters.join(",");

  // Dropdown options reflect what's actually present in the flags for the selected status.
  useEffect(() => {
    fetchFilterOptions();
  }, [statusFilter]);

  // Debounce the free-text search so we don't hit the API on every keystroke. Skip the very
  // first run (mount) so a restored search value doesn't reset the restored page.
  const firstSearchRun = useRef(true);
  useEffect(() => {
    if (firstSearchRun.current) { firstSearchRun.current = false; return; }
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Persist the view state whenever any tracked piece changes.
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        viewMode, statusFilter, searchQuery, priorityFilters, flagTypeFilters,
        sellerFilters, shippingPartnerFilters, actionFilter, resolutionFilter, filtersExpanded,
        page: pagination.page,
      }));
    } catch { /* ignore quota / private-mode errors */ }
  }, [viewMode, statusFilter, searchQuery, priorityFilters, flagTypeFilters,
      sellerFilters, shippingPartnerFilters, actionFilter, resolutionFilter, filtersExpanded, pagination.page]);

  useEffect(() => {
    fetchFlags();
    fetchFlagTypes();
    fetchRecipients();
    fetchAvailableActions();
    fetchSettings();
    fetchStatusCounts();
  }, [statusFilter, priKey, ftKey, actionFilter, resolutionFilter, debouncedSearch, sellerKey, spKey, pagination.page]);

  // Grouped views (LRN / Client) need the full matching set — re-fetch on view/filter/tab change.
  useEffect(() => {
    if (viewMode !== "list") fetchAllFlags();
    // eslint-disable-next-line
  }, [viewMode, statusFilter, priKey, ftKey, actionFilter, resolutionFilter, debouncedSearch, sellerKey, spKey]);

  const fetchStatusCounts = async () => {
    try {
      // Build shared filter params (excluding status, which varies per tab)
      const buildParams = (status) => {
        const params = new URLSearchParams();
        params.append("status", status);
        params.append("limit", 1);
        if (priorityFilters.length) params.append("priority", priorityFilters.join(","));
        if (flagTypeFilters.length) params.append("flag_type_id", flagTypeFilters.join(","));
        if (actionFilter && actionFilter !== "all") params.append("has_actions", actionFilter === "action_taken" ? "true" : "false");
        if (debouncedSearch && debouncedSearch.trim()) params.append("search", debouncedSearch.trim());
        if (sellerFilters.length) params.append("seller_id", sellerFilters.join(","));
        if (shippingPartnerFilters.length) params.append("shipping_partner_id", shippingPartnerFilters.join(","));
        return params.toString();
      };

      const [activeRes, resolvedRes, ignoredRes] = await Promise.all([
        axios.get(`${API}/red-flags?${buildParams("active")}`),
        axios.get(`${API}/red-flags?${buildParams("resolved")}`),
        axios.get(`${API}/red-flags?${buildParams("ignored")}`)
      ]);
      setStatusCounts({
        active: activeRes.data.total || 0,
        resolved: resolvedRes.data.total || 0,
        ignored: ignoredRes.data.total || 0
      });
    } catch (error) {
      console.error("Failed to fetch status counts");
    }
  };

  // Shared filter params (no paging) — used by the list fetch and the grouped-view fetch.
  const buildFlagParams = () => {
    const params = new URLSearchParams();
    if (statusFilter) params.append("status", statusFilter);
    if (priorityFilters.length) params.append("priority", priorityFilters.join(","));
    if (flagTypeFilters.length) params.append("flag_type_id", flagTypeFilters.join(","));
    if (actionFilter && actionFilter !== "all") params.append("has_actions", actionFilter === "action_taken" ? "true" : "false");
    // Resolution source only applies to resolved/ignored flags (active flags have no resolver).
    if (statusFilter !== "active" && resolutionFilter && resolutionFilter !== "all") params.append("resolution", resolutionFilter);
    if (debouncedSearch && debouncedSearch.trim()) params.append("search", debouncedSearch.trim());
    if (sellerFilters.length) params.append("seller_id", sellerFilters.join(","));
    if (shippingPartnerFilters.length) params.append("shipping_partner_id", shippingPartnerFilters.join(","));
    return params;
  };

  const fetchFlags = async () => {
    setLoading(true);
    try {
      const params = buildFlagParams();
      params.append("page", pagination.page);
      params.append("limit", pagination.limit);

      const response = await axios.get(`${API}/red-flags?${params.toString()}`);
      setFlags(response.data.flags);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        pages: response.data.pages
      }));
    } catch (error) {
      toast.error("Failed to fetch red flags");
    } finally {
      setLoading(false);
    }
  };

  // Fetch ALL matching flags (the red_flags collection is bounded) for the grouped views.
  const fetchAllFlags = async () => {
    setAllFlagsLoading(true);
    try {
      const acc = [];
      let page = 1;
      let total = Infinity;
      while (acc.length < total && page <= 50) {
        const params = buildFlagParams();
        params.append("page", page);
        params.append("limit", 100);
        const res = await axios.get(`${API}/red-flags?${params.toString()}`);
        const batch = res.data.flags || [];
        acc.push(...batch);
        total = res.data.total || 0;
        if (batch.length === 0) break;
        page += 1;
      }
      setAllFlags(acc);
    } catch (error) {
      toast.error("Failed to load flags for grouping");
    } finally {
      setAllFlagsLoading(false);
    }
  };

  const fetchFlagTypes = async () => {
    try {
      const response = await axios.get(`${API}/red-flags/types/all`);
      setFlagTypes(response.data);
    } catch (error) {
      console.error("Failed to fetch flag types");
    }
  };

  const fetchRecipients = async () => {
    try {
      const response = await axios.get(`${API}/red-flags/recipients/all`);
      setRecipients(response.data);
    } catch (error) {
      console.error("Failed to fetch recipients");
    }
  };

  const fetchAvailableActions = async () => {
    try {
      const response = await axios.get(`${API}/red-flags/actions/available`);
      setAvailableActions(response.data);
    } catch (error) {
      console.error("Failed to fetch available actions");
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/red-flags/settings`);
      setSettings(response.data);
      setCheckInterval(response.data.check_interval_minutes || 15);
    } catch (error) {
      console.error("Failed to fetch settings");
    }
  };

  const handleUpdateSettings = async () => {
    if (checkInterval < 1 || checkInterval > 1440) {
      toast.error("Interval must be between 1 and 1440 minutes (24 hours)");
      return;
    }
    
    setSavingSettings(true);
    try {
      const response = await axios.put(`${API}/red-flags/settings`, {
        check_interval_minutes: checkInterval
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchSettings();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleTriggerCheck = async () => {
    setTriggeringCheck(true);
    try {
      const response = await axios.post(`${API}/red-flags/trigger-check`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchFlags();
        fetchSettings();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to trigger check");
    } finally {
      setTriggeringCheck(false);
    }
  };

  const handleTakeAction = async () => {
    if (!actionType) {
      toast.error("Please select an action");
      return;
    }
    
    setSubmittingAction(true);
    try {
      const response = await axios.post(`${API}/red-flags/${selectedFlag.id}/action`, {
        action_type: actionType,
        notes: actionNotes
      });
      
      if (response.data.success) {
        toast.success(`Action '${actionType}' recorded successfully`);
        setActionDialogOpen(false);
        setSelectedFlag(null);
        setActionType("");
        setActionNotes("");
        fetchFlags();
      }
    } catch (error) {
      toast.error("Failed to record action");
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleUpdateFlagType = async () => {
    if (!editingFlagType) return;
    
    setSavingConfig(true);
    try {
      const response = await axios.put(`${API}/red-flags/types/${editingFlagType.id}`, {
        enabled: editingFlagType.enabled,
        threshold_time: editingFlagType.threshold_time,
        threshold_value: editingFlagType.threshold_value,
        priority: editingFlagType.priority
      });
      
      if (response.data.success) {
        toast.success("Flag configuration updated");
        fetchFlagTypes();
        setConfigDialogOpen(false);
        setEditingFlagType(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleAddRecipient = async () => {
    if (!newRecipientEmail) {
      toast.error("Please enter an email");
      return;
    }
    
    setSavingRecipient(true);
    try {
      const response = await axios.post(`${API}/red-flags/recipients`, {
        email: newRecipientEmail,
        name: newRecipientName,
        flag_types: []
      });
      
      if (response.data.success) {
        toast.success("Recipient added");
        fetchRecipients();
        setRecipientDialogOpen(false);
        setNewRecipientEmail("");
        setNewRecipientName("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add recipient");
    } finally {
      setSavingRecipient(false);
    }
  };

  const handleDeleteRecipient = async (recipientId) => {
    try {
      const response = await axios.delete(`${API}/red-flags/recipients/${recipientId}`);
      if (response.data.success) {
        toast.success("Recipient deleted");
        fetchRecipients();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete recipient");
    }
  };

  const handleToggleRecipient = async (recipientId, enabled) => {
    try {
      await axios.put(`${API}/red-flags/recipients/${recipientId}?enabled=${enabled}`);
      fetchRecipients();
    } catch (error) {
      toast.error("Failed to update recipient");
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/red-flags/filter-options`, { params: { status: statusFilter } });
      setFilterOptions(response.data || { sellers: [], shipping_partners: [], flag_types: [], priorities: [], summary: { total: 0, by_flag_type: [], by_seller: [] } });
    } catch (error) {
      console.error("Failed to fetch filter options");
    }
  };

  // The list flag already carries enrichment + action_logs + details, so the modal opens
  // instantly from it (no extra round-trip).
  const handleViewDetail = (flag) => {
    setFlagDetail(flag);
    setDetailDialogOpen(true);
  };

  // A single flag card — reused by the list view AND the grouped (LRN / Client) views so they
  // render identically.
  const renderCard = (flag) => {
    const en = flag.enrichment || {};
    const orderUrl = orderDetailUrl(flag);
    const appt = apptDisplay(en);
    return (
      <Card
        key={flag.id}
        onClick={() => handleViewDetail(flag)}
        className={`border-l-4 cursor-pointer transition-shadow hover:shadow-md ${flag.priority === 'critical' ? 'border-l-red-500' : flag.priority === 'high' ? 'border-l-orange-500' : flag.priority === 'medium' ? 'border-l-yellow-500' : 'border-l-slate-300'}`}
        data-testid="red-flag-card"
      >
        <CardContent className="py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge className={PRIORITY_COLORS[flag.priority]}>{flag.priority?.toUpperCase()}</Badge>
                <Badge className={STATUS_COLORS[flag.status]}>{flag.status?.toUpperCase()}</Badge>
                {flag.action_logs?.length > 0 && (
                  <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    {flag.action_logs.length} Action{flag.action_logs.length > 1 ? 's' : ''} Taken
                  </Badge>
                )}
                {flag.is_merged && flag.merged_reasons?.length > 1 && (
                  <Badge className="bg-purple-100 text-purple-700 flex items-center gap-1">
                    <Combine className="w-3 h-3" />
                    Merged · {flag.merged_reasons.length} reasons
                  </Badge>
                )}
                <Badge variant="outline">{flag.flag_type_name}</Badge>
              </div>
              <h3 className="font-semibold text-slate-800 mb-1">{flag.title}</h3>
              {flag.is_merged && flag.merged_reasons?.length > 1 ? (
                <div className="mb-3">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">All flag reasons</div>
                  <ul className="space-y-1.5">
                    {flag.merged_reasons.map((r, i) => (
                      <li key={r.flag_type_id || i} className="flex items-start gap-2">
                        <Badge className={`${PRIORITY_COLORS[r.priority] || ''} shrink-0`}>{r.flag_type_name}</Badge>
                        <span className="text-sm text-slate-600">{r.description || r.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-slate-600 mb-3">{flag.description}</p>
              )}

              {/* Order context — Order & LRN open the order detail page in a new tab */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2 mb-3">
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">Order</div>
                  {en.order_number ? (
                    <a href={orderUrl} target="_blank" rel="noopener noreferrer" onClick={stop} className="text-sm font-medium text-blue-600 hover:underline">{en.order_number}</a>
                  ) : <span className="text-sm text-slate-400">—</span>}
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">LRN</div>
                  {en.lrn ? (
                    <a href={orderUrl} target="_blank" rel="noopener noreferrer" onClick={stop} className="text-sm font-medium text-blue-600 hover:underline">{en.lrn}</a>
                  ) : <span className="text-sm text-slate-400">—</span>}
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">Seller</div>
                  <div className="text-sm font-medium text-slate-700 truncate" title={en.seller_name || ''}>{en.seller_name || '—'}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">Shipping Partner</div>
                  <div className="text-sm font-medium text-slate-700 truncate" title={en.shipping_partner_name || ''}>{en.shipping_partner_name || '—'}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">Appointment</div>
                  <div className="text-sm font-medium text-slate-700">{appt || '—'}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">PO Expiry</div>
                  <div className="text-sm font-medium text-slate-700">{en.po_expiry_date || '—'}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">EDD</div>
                  <div className="text-sm font-medium text-slate-700">{en.edd || '—'}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">Live Status</div>
                  {en.live_status ? <Badge className="bg-slate-100 text-slate-700">{en.live_status}</Badge> : <span className="text-sm text-slate-400">—</span>}
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDateIST(flag.created_at, 'full')}
                </span>
              </div>
              {flag.action_logs?.length > 0 && (() => {
                const latest = latestActionLog(flag.action_logs);
                return (
                  <div className="mt-2 p-3 bg-blue-50 rounded-md border border-blue-100">
                    <div className="text-sm text-blue-700 font-medium">Latest Action: {latest?.action_type}</div>
                    <div className="text-sm text-blue-600 mt-0.5">{latest?.notes}</div>
                    <div className="text-xs text-blue-500 mt-0.5">
                      By {latest?.action_by_name} • {formatDateIST(latest?.action_at, 'short')}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0" onClick={stop}>
              <Button variant="ghost" size="sm" onClick={() => handleViewDetail(flag)} title="View details">
                <Eye className="w-4 h-4" />
              </Button>
              {flag.status === 'active' && (
                <Button size="sm" onClick={() => { setSelectedFlag(flag); setActionDialogOpen(true); }}>
                  Take Action
                </Button>
              )}
              {(en.order_id || flag.entity_id) && (
                <Button variant="outline" size="sm" asChild title="Open order in new tab">
                  <a href={orderUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Group the full flag set by LRN or by Client (seller); largest groups first.
  // A card's real flag count: a merged card stands in for all its reasons, a plain card is 1.
  const flagReasonCount = (f) => (f?.is_merged && f?.merged_reasons?.length ? f.merged_reasons.length : 1);

  const buildGroups = (list, mode) => {
    const map = {};
    (list || []).forEach((f) => {
      const en = f.enrichment || {};
      let key, label, sub;
      if (mode === "lrn") {
        key = en.lrn || en.order_number || f.entity_id || "no-lrn";
        label = en.lrn || en.order_number || "No LRN";
        sub = en.lrn && en.order_number ? en.order_number : (en.seller_name || "");
      } else {
        key = en.seller_id || en.seller_name || "unknown";
        label = en.seller_name || en.seller_id || "Unknown client";
        sub = "";
      }
      if (!map[key]) map[key] = { label, sub, flags: [], flagCount: 0 };
      map[key].flags.push(f);
      map[key].flagCount += flagReasonCount(f);   // count reasons, not cards (merged = N)
    });
    return Object.entries(map).sort((a, b) => b[1].flagCount - a[1].flagCount);
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Flag className="w-7 h-7 text-red-500" />
              Ops Red Flags
            </h1>
            <p className="text-slate-500 mt-1">Monitor and resolve day operations issues</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canBulkResolve && (
              <Button
                onClick={handleDedupe}
                variant="outline"
                size="sm"
                disabled={deduping}
                className="text-amber-700 border-amber-200 hover:bg-amber-50"
                title="Remove duplicate active flags for the same order + flag type"
              >
                {deduping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Remove Duplicates
              </Button>
            )}
            {canBulkResolve && (
              <Button
                onClick={handleCleanupReachStuck}
                variant="outline"
                size="sm"
                disabled={cleaningStuck}
                className="text-orange-700 border-orange-200 hover:bg-orange-50"
                title="Clean redundant 'Order Stuck in Same Status' flags for REACH_DESTINATION (already covered by 'Stuck at Last Mile Center')"
              >
                {cleaningStuck ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eraser className="w-4 h-4 mr-2" />}
                Clean Reach-Dest Stuck
              </Button>
            )}
            {canBulkResolve && (
              <Button
                onClick={handleMergeFlags}
                variant="outline"
                size="sm"
                disabled={merging}
                className="text-purple-700 border-purple-200 hover:bg-purple-50"
                title="Merge every LRN's multiple flags into one consolidated flag (all reasons + comments combined)"
              >
                {merging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Combine className="w-4 h-4 mr-2" />}
                Merge Flags
              </Button>
            )}
            {canBulkResolve && (
              <Button
                onClick={openBulkResolve}
                variant="outline"
                size="sm"
                disabled={bulkResolving}
                className="text-green-700 border-green-200 hover:bg-green-50"
                title="Resolve all active flags whose orders are Delivered / Return Delivered"
              >
                {bulkResolving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Resolve Delivered
              </Button>
            )}
            <Button onClick={fetchFlags} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Bulk-resolve delivered — preview modal */}
        <Dialog open={bulkModalOpen} onOpenChange={setBulkModalOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Resolve delivered flags</DialogTitle>
              <DialogDescription>
                These active flags are on orders already Delivered / Return Delivered and will be marked Resolved.
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto -mx-1 px-1">
              {bulkPreviewLoading ? (
                <div className="py-10 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…
                </div>
              ) : bulkPreview.count === 0 ? (
                <div className="py-10 text-center text-slate-500">No active flags on delivered orders.</div>
              ) : (
                <div className="space-y-2">
                  {bulkPreview.flags.map((r) => {
                    const isReturn = r.order_status === "RETURN_DELIVERED";
                    const when = isReturn ? r.return_delivered_at : r.delivered_at;
                    return (
                      <div key={r.flag_id} className="rounded-lg border border-slate-200 p-3">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className="font-medium text-slate-800 text-sm">{r.order_number || "—"}</span>
                          <Badge className={isReturn ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}>
                            {isReturn ? "Return Delivered" : "Delivered"}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                          <div><span className="text-slate-400">LRN:</span> {r.lrn || "—"}</div>
                          <div><span className="text-slate-400">Seller:</span> {r.seller_name || "—"}</div>
                          <div><span className="text-slate-400">Flag:</span> {r.flag_type_name || "—"}</div>
                          <div>
                            <span className="text-slate-400">{isReturn ? "Return Delivered" : "Delivered"}:</span>{" "}
                            {when ? formatDateIST(when, "short") : "—"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkModalOpen(false)}>Cancel</Button>
              <Button
                onClick={confirmBulkResolve}
                disabled={bulkResolving || bulkPreview.count === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                {bulkResolving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Resolve {bulkPreview.count} flag{bulkPreview.count === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Tabs defaultValue="flags" className="space-y-6">
          <TabsList>
            <TabsTrigger value="flags" className="flex items-center gap-2">
              <Flag className="w-4 h-4" />
              Flags
            </TabsTrigger>
            {isAdmin && (
              <>
                <TabsTrigger value="config" className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Configuration
                </TabsTrigger>
                <TabsTrigger value="recipients" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Recipients
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Flags Tab */}
          <TabsContent value="flags" className="space-y-6">
            {/* Status Tabs */}
            <div className="flex border-b border-slate-200">
              <button
                onClick={() => { setStatusFilter("active"); setPagination(prev => ({ ...prev, page: 1 })); }}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  statusFilter === "active"
                    ? "border-red-500 text-red-600 bg-red-50"
                    : "border-transparent text-slate-600 hover:text-slate-800 hover:bg-slate-50"
                }`}
                data-testid="active-flags-tab"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Active
                  <Badge className="bg-red-100 text-red-700 text-xs px-1.5 py-0.5">{statusCounts.active}</Badge>
                </div>
              </button>
              <button
                onClick={() => { setStatusFilter("resolved"); setPagination(prev => ({ ...prev, page: 1 })); }}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  statusFilter === "resolved"
                    ? "border-green-500 text-green-600 bg-green-50"
                    : "border-transparent text-slate-600 hover:text-slate-800 hover:bg-slate-50"
                }`}
                data-testid="resolved-flags-tab"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Resolved
                  <Badge className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5">{statusCounts.resolved}</Badge>
                </div>
              </button>
              <button
                onClick={() => { setStatusFilter("ignored"); setPagination(prev => ({ ...prev, page: 1 })); }}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  statusFilter === "ignored"
                    ? "border-slate-500 text-slate-600 bg-slate-50"
                    : "border-transparent text-slate-600 hover:text-slate-800 hover:bg-slate-50"
                }`}
                data-testid="ignored-flags-tab"
              >
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Ignored
                  <Badge className="bg-slate-100 text-slate-700 text-xs px-1.5 py-0.5">{statusCounts.ignored}</Badge>
                </div>
              </button>
            </div>

            {/* Search + Filters */}
            <Card>
              <CardContent className="pt-4 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by Order ID, PO / Reference No., Edgistify ID, or LRN..."
                      className="pl-9 h-10"
                      data-testid="red-flags-search"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0 relative"
                    onClick={() => setFiltersExpanded(v => !v)}
                    title={filtersExpanded ? "Hide filters" : "Show filters"}
                    aria-pressed={filtersExpanded}
                    data-testid="toggle-filters"
                  >
                    <Filter className={`w-4 h-4 ${filtersExpanded ? "text-[#00A699]" : ""}`} />
                    {activeFilterCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold h-4 min-w-[1rem] px-1">{activeFilterCount}</span>
                    )}
                  </Button>
                </div>

                {/* Per-tab summary — counts by error type and by client. Click a chip to filter. */}
                {((filterOptions.summary?.by_flag_type?.length || 0) > 0 || (filterOptions.summary?.by_seller?.length || 0) > 0) && (
                  <div className="space-y-3 border-t border-slate-100 pt-3">
                    <div className="text-xs text-slate-500">
                      <span className="font-semibold text-slate-700">{filterOptions.summary?.total || 0}</span>{" "}
                      {statusFilter} flag{(filterOptions.summary?.total || 0) === 1 ? "" : "s"} in this tab
                    </div>
                    {(filterOptions.summary?.by_flag_type?.length || 0) > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">By Flag Type</p>
                        <div className="flex flex-wrap gap-2">
                          {filterOptions.summary.by_flag_type.map((t) => {
                            const selected = flagTypeFilters.includes(t.id);
                            return (
                              <button
                                key={t.id}
                                type="button"
                                onClick={() => { setFlagTypeFilters((prev) => prev.includes(t.id) ? prev.filter((x) => x !== t.id) : [...prev, t.id]); resetPage(); }}
                                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${selected ? "bg-red-50 border-red-300 text-red-700" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}
                              >
                                {t.name}
                                <span className={`inline-flex items-center justify-center rounded-full h-4 min-w-[1rem] px-1 text-[10px] font-semibold ${selected ? "bg-red-500 text-white" : "bg-slate-100 text-slate-600"}`}>{t.count}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {(filterOptions.summary?.by_seller?.length || 0) > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">By Client</p>
                        <div className="flex flex-wrap gap-2">
                          {filterOptions.summary.by_seller.map((s) => {
                            const selected = sellerFilters.includes(s.id);
                            return (
                              <button
                                key={s.id}
                                type="button"
                                onClick={() => { setSellerFilters((prev) => prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id]); resetPage(); }}
                                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${selected ? "bg-teal-50 border-teal-300 text-teal-700" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}
                              >
                                {s.name}
                                <span className={`inline-flex items-center justify-center rounded-full h-4 min-w-[1rem] px-1 text-[10px] font-semibold ${selected ? "bg-teal-500 text-white" : "bg-slate-100 text-slate-600"}`}>{s.count}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {filtersExpanded && (
                <div className="flex flex-wrap gap-4">
                  {statusFilter === "active" && (
                    <div className="w-44">
                      <Label className="text-xs">Action Status</Label>
                      <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); resetPage(); }}>
                        <SelectTrigger className="h-9 mt-1" data-testid="action-filter-select">
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="needs_attention">Needs Attention</SelectItem>
                          <SelectItem value="action_taken">Action Taken</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {statusFilter !== "active" && (
                    <div className="w-44">
                      <Label className="text-xs">Resolution</Label>
                      <Select value={resolutionFilter} onValueChange={(v) => { setResolutionFilter(v); resetPage(); }}>
                        <SelectTrigger className="h-9 mt-1" data-testid="resolution-filter-select">
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="auto">Auto-resolved (system)</SelectItem>
                          <SelectItem value="manual">Manually resolved</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="w-44">
                    <Label className="text-xs">Priority</Label>
                    <div className="mt-1">
                      <SearchableMultiSelect
                        values={priorityFilters}
                        onChange={(vals) => { setPriorityFilters(vals); resetPage(); }}
                        options={filterOptions.priorities.map(p => ({ value: p, label: PRIORITY_LABELS[p] || p }))}
                        placeholder="All Priorities"
                        emptyText="No priorities in this list"
                      />
                    </div>
                  </div>
                  <div className="w-56">
                    <Label className="text-xs">Flag Type</Label>
                    <div className="mt-1">
                      <SearchableMultiSelect
                        values={flagTypeFilters}
                        onChange={(vals) => { setFlagTypeFilters(vals); resetPage(); }}
                        options={filterOptions.flag_types}
                        placeholder="All Types"
                        emptyText="No flag types in this list"
                      />
                    </div>
                  </div>
                  <div className="w-60" data-testid="seller-filter-select">
                    <Label className="text-xs">Seller</Label>
                    <div className="mt-1">
                      <SearchableMultiSelect
                        values={sellerFilters}
                        onChange={(vals) => { setSellerFilters(vals); resetPage(); }}
                        options={filterOptions.sellers}
                        placeholder="All Sellers"
                        emptyText="No sellers in this list"
                      />
                    </div>
                  </div>
                  <div className="w-56">
                    <Label className="text-xs">Shipping Partner</Label>
                    <div className="mt-1">
                      <SearchableMultiSelect
                        values={shippingPartnerFilters}
                        onChange={(vals) => { setShippingPartnerFilters(vals); resetPage(); }}
                        options={filterOptions.shipping_partners}
                        placeholder="All Partners"
                        emptyText="No partners in this list"
                      />
                    </div>
                  </div>
                  {(searchQuery || sellerFilters.length || shippingPartnerFilters.length || priorityFilters.length || flagTypeFilters.length) ? (
                    <div className="flex items-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 text-slate-600"
                        onClick={() => {
                          setSearchQuery("");
                          setSellerFilters([]);
                          setShippingPartnerFilters([]);
                          setPriorityFilters([]);
                          setFlagTypeFilters([]);
                          setActionFilter("all");
                          resetPage();
                        }}
                      >
                        Clear all
                      </Button>
                    </div>
                  ) : null}
                </div>
                )}
              </CardContent>
            </Card>

            {/* View toggle — flat list, or grouped by LRN / Client */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-slate-500">View:</span>
              {[["list", "List"], ["lrn", "By LRN"], ["client", "By Client"]].map(([v, label]) => (
                <Button
                  key={v}
                  variant={viewMode === v ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode(v)}
                  className={viewMode === v ? "bg-[#00A699] hover:bg-[#008f84]" : ""}
                >
                  {label}
                </Button>
              ))}
            </div>

            {/* Flags — flat list or grouped by LRN / Client (grouped views load the full set) */}
            {(viewMode === "list" ? loading : allFlagsLoading) ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (viewMode === "list" ? flags.length === 0 : allFlags.length === 0) ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700">No Red Flags</h3>
                  <p className="text-slate-500">All operations are running smoothly!</p>
                </CardContent>
              </Card>
            ) : viewMode === "list" ? (
              <div className="space-y-3">
                {flags.map(renderCard)}

                {/* Pagination — page number + controls aligned to the left */}
                {pagination.pages > 1 && (
                  <div className="flex flex-wrap items-center gap-4 py-4">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" disabled={pagination.page === 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-slate-600">Page {pagination.page} of {pagination.pages}</span>
                      <Button variant="outline" size="sm" disabled={pagination.page === pagination.pages}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-slate-500">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {buildGroups(allFlags, viewMode).map(([key, group]) => (
                  <div key={key} className="space-y-3">
                    <div className="flex items-center gap-2 pb-1.5 border-b border-slate-200">
                      <span className="text-[11px] uppercase tracking-wide text-slate-400">{viewMode === "lrn" ? "LRN" : "Client"}</span>
                      <span className="font-semibold text-slate-800">{group.label}</span>
                      {group.sub ? <span className="text-sm text-slate-500">· {group.sub}</span> : null}
                      <Badge className="bg-slate-100 text-slate-700" title={`${group.flagCount} flag${group.flagCount === 1 ? '' : 's'}`}>{group.flagCount}</Badge>
                    </div>
                    <div className="space-y-3">
                      {group.flags.map(renderCard)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Configuration Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="config" className="space-y-6">
              {/* Check Interval Settings Card */}
              <Card className="border-l-4 border-l-[#00A699]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#00A699]" />
                    Check Schedule Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap items-end gap-4">
                    <div className="flex-1 min-w-[200px] max-w-[300px]">
                      <Label className="text-sm">Check Interval (minutes)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          min={1}
                          max={1440}
                          value={checkInterval}
                          onChange={(e) => setCheckInterval(parseInt(e.target.value) || 15)}
                          className="h-9 w-24"
                        />
                        <span className="text-sm text-slate-500">minutes</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Range: 1 min - 1440 min (24 hours)</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleUpdateSettings}
                        disabled={savingSettings || checkInterval === settings?.check_interval_minutes}
                        className="bg-[#00A699] hover:bg-[#008f84]"
                      >
                        {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Save Interval
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleTriggerCheck}
                        disabled={triggeringCheck}
                      >
                        {triggeringCheck ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        Run Check Now
                      </Button>
                    </div>
                  </div>
                  {settings && (
                    <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t text-sm text-slate-600">
                      <div>
                        <span className="text-slate-500">Current Interval:</span>{' '}
                        <span className="font-medium">{settings.check_interval_minutes} min</span>
                      </div>
                      {settings.last_check_at && (
                        <div>
                          <span className="text-slate-500">Last Check:</span>{' '}
                          <span className="font-medium">{formatDateIST(settings.last_check_at, 'full')}</span>
                        </div>
                      )}
                      {settings.updated_by_name && (
                        <div>
                          <span className="text-slate-500">Updated by:</span>{' '}
                          <span className="font-medium">{settings.updated_by_name}</span>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Flag Type Configuration Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Flag Type Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {flagTypes.map(type => (
                      <div key={type.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-slate-800">{type.name}</h4>
                            <Badge className={PRIORITY_COLORS[type.priority]}>{type.priority}</Badge>
                            {!type.enabled && <Badge variant="outline">Disabled</Badge>}
                          </div>
                          <p className="text-sm text-slate-500">{type.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            {type.threshold_time && <span>Time: {type.threshold_time}</span>}
                            {type.threshold_value && <span>Threshold: {type.threshold_value}</span>}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingFlagType({ ...type });
                            setConfigDialogOpen(true);
                          }}
                        >
                          <Settings className="w-4 h-4 mr-1" />
                          Configure
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Email Recipients Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="recipients" className="space-y-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Email Recipients</CardTitle>
                  <Button size="sm" onClick={() => setRecipientDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Recipient
                  </Button>
                </CardHeader>
                <CardContent>
                  {recipients.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No email recipients configured</p>
                  ) : (
                    <div className="space-y-3">
                      {recipients.map(recipient => (
                        <div key={recipient.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-800">{recipient.name || recipient.email}</p>
                            {recipient.name && <p className="text-sm text-slate-500">{recipient.email}</p>}
                            <p className="text-xs text-slate-400 mt-1">
                              Added by {recipient.created_by_name} on {formatDateIST(recipient.created_at, 'short')}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={recipient.enabled}
                                onCheckedChange={(checked) => handleToggleRecipient(recipient.id, checked)}
                              />
                              <span className="text-sm text-slate-500">{recipient.enabled ? 'Active' : 'Disabled'}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteRecipient(recipient.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Take Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Take Action on Red Flag</DialogTitle>
            <DialogDescription>
              {selectedFlag?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Action Type</Label>
              <Select value={actionType} onValueChange={setActionType}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
                <SelectContent>
                  {availableActions.map(action => (
                    <SelectItem key={action} value={action}>{action}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder="Add any notes about this action..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleTakeAction} disabled={submittingAction}>
              {submittingAction ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configure Flag Type Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure Flag Type</DialogTitle>
            <DialogDescription>
              {editingFlagType?.name}
            </DialogDescription>
          </DialogHeader>
          {editingFlagType && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <Label>Enabled</Label>
                <Switch
                  checked={editingFlagType.enabled}
                  onCheckedChange={(checked) => setEditingFlagType(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select 
                  value={editingFlagType.priority} 
                  onValueChange={(value) => setEditingFlagType(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editingFlagType.threshold_time !== null && (
                <div>
                  <Label>Threshold Time (HH:MM)</Label>
                  <Input
                    type="time"
                    value={editingFlagType.threshold_time || ""}
                    onChange={(e) => setEditingFlagType(prev => ({ ...prev, threshold_time: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              )}
              {editingFlagType.threshold_value !== null && (
                <div>
                  <Label>Threshold Value</Label>
                  <Input
                    type="number"
                    value={editingFlagType.threshold_value || ""}
                    onChange={(e) => setEditingFlagType(prev => ({ ...prev, threshold_value: parseFloat(e.target.value) }))}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">{editingFlagType.description}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateFlagType} disabled={savingConfig}>
              {savingConfig ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Recipient Dialog */}
      <Dialog open={recipientDialogOpen} onOpenChange={setRecipientDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Email Recipient</DialogTitle>
            <DialogDescription>
              Add an email address to receive red flag notifications
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={newRecipientEmail}
                onChange={(e) => setNewRecipientEmail(e.target.value)}
                placeholder="email@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Name (optional)</Label>
              <Input
                value={newRecipientName}
                onChange={(e) => setNewRecipientName(e.target.value)}
                placeholder="John Doe"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecipientDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddRecipient} disabled={savingRecipient}>
              {savingRecipient ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Recipient
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flag Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Red Flag Details</DialogTitle>
          </DialogHeader>
          {flagDetail && (
            <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-1 -mr-1">
              <div className="flex items-center gap-2">
                <Badge className={PRIORITY_COLORS[flagDetail.priority]}>{flagDetail.priority?.toUpperCase()}</Badge>
                <Badge className={STATUS_COLORS[flagDetail.status]}>{flagDetail.status?.toUpperCase()}</Badge>
              </div>
              <div>
                <h3 className="font-semibold text-lg">{flagDetail.title}</h3>
                {flagDetail.is_merged && flagDetail.merged_reasons?.length > 1 ? (
                  <div className="mt-2">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">All flag reasons ({flagDetail.merged_reasons.length})</div>
                    <ul className="space-y-1.5">
                      {flagDetail.merged_reasons.map((r, i) => (
                        <li key={r.flag_type_id || i} className="flex items-start gap-2">
                          <Badge className={`${PRIORITY_COLORS[r.priority] || ''} shrink-0`}>{r.flag_type_name}</Badge>
                          <span className="text-sm text-slate-600">{r.description || r.title}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-slate-600">{flagDetail.description}</p>
                )}
              </div>

              {flagDetail.enrichment && (
                <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 p-4 rounded-lg">
                  <div>
                    <div className="text-xs text-slate-500">Order</div>
                    {flagDetail.enrichment.order_number ? (
                      <a href={orderDetailUrl(flagDetail)} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline">{flagDetail.enrichment.order_number}</a>
                    ) : <span className="font-medium">—</span>}
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">LRN</div>
                    {flagDetail.enrichment.lrn ? (
                      <a href={orderDetailUrl(flagDetail)} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline">{flagDetail.enrichment.lrn}</a>
                    ) : <span className="font-medium">—</span>}
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Seller</div>
                    <div className="font-medium">{flagDetail.enrichment.seller_name || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Shipping Partner</div>
                    <div className="font-medium">{flagDetail.enrichment.shipping_partner_name || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Appointment</div>
                    <div className="font-medium">{apptDisplay(flagDetail.enrichment) || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">PO Expiry Date</div>
                    <div className="font-medium">{flagDetail.enrichment.po_expiry_date || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">EDD</div>
                    <div className="font-medium">{flagDetail.enrichment.edd || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Live Status</div>
                    <div className="font-medium">{flagDetail.enrichment.live_status || '—'}</div>
                  </div>
                </div>
              )}

              {flagDetail.details && Object.keys(flagDetail.details).length > 0 && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">Additional Details</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(flagDetail.details).map(([key, value]) => (
                      <div key={key}>
                        <span className="text-slate-500">{key.replace(/_/g, ' ')}:</span>{' '}
                        <span className="font-medium">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {flagDetail.action_logs && flagDetail.action_logs.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2">Action History</h4>
                  <div className="space-y-2">
                    {flagDetail.action_logs.map((log, idx) => (
                      <div key={log.id || idx} className="bg-slate-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                          <Badge variant="outline">{log.action_type}</Badge>
                          <span className="text-xs text-slate-500">{formatDateIST(log.action_at, 'full')}</span>
                        </div>
                        {log.notes && <p className="text-sm text-slate-600 mt-2">{log.notes}</p>}
                        <p className="text-xs text-slate-400 mt-1">By {log.action_by_name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {flagDetail.resolved_at && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm">
                    <span className="font-medium text-green-700">Resolved:</span>{' '}
                    {formatDateIST(flagDetail.resolved_at, 'full')} by {flagDetail.resolved_by_name}
                  </p>
                  {flagDetail.resolution_action && (
                    <p className="text-sm text-green-600">Action: {flagDetail.resolution_action}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default RedFlags;
